<template>
    <div class="modal-card cartera-modal">
        <header class="modal-card-head has-background-white">
            <h3 class="modal-card-title has-text-centered">
                Cartera por analista
            </h3>
            <button class="delete" aria-label="Cerrar Modal" @click="$emit('close')"></button>
        </header>

        <!-- Analista dueño de la cartera (preseleccionado: quien abre el modal) -->
        <section class="section-analysts">
            <p class="col-eyebrow">Analista</p>
            <div class="analyst-chips" role="radiogroup" aria-label="Analista">
                <button
                    v-for="user in orderedAnalysts"
                    :key="user.user_id"
                    type="button"
                    class="analyst-chip"
                    :class="{ 'is-selected': user.user_id === selectedAnalystId }"
                    role="radio"
                    :aria-checked="user.user_id === selectedAnalystId ? 'true' : 'false'"
                    @click="selectAnalyst(user)"
                >
                    {{ user.user_email }}<span v-if="isCurrentUser(user)" class="chip-me">tú</span>
                </button>
            </div>
        </section>

        <!-- Cartera resultante | buscador para agregar -->
        <section class="section-body">
            <div v-if="selectedAnalystId === -1" class="empty-state empty-state--body">
                Elige un analista para ver su cartera.
            </div>
            <template v-else>
                <div class="col col-cartera">
                    <p class="col-eyebrow">Cartera ({{ carteraCountLabel }})</p>
                    <div class="col-scroll">
                        <div v-if="carteraView.length === 0" class="empty-state">
                            Sin empresas asignadas. Busca a la derecha y agrega con «Agregar».
                        </div>
                        <div
                            v-for="item in carteraView"
                            :key="item.company.c_id"
                            class="company-row"
                            :class="{ 'is-added': item.state === 'nueva', 'is-removed': item.state === 'baja' }"
                        >
                            <div class="company-row__info">
                                <span class="company-row__name">{{ item.company.name }}</span>
                                <span class="company-row__meta">{{ item.company.rut }} · {{ item.company.email || 'sin usuarios' }}</span>
                            </div>
                            <span v-if="item.state === 'nueva'" class="state-tag state-tag--add">nueva</span>
                            <button
                                v-if="item.state === 'actual'"
                                type="button"
                                class="row-action row-action--remove"
                                @click="toggleRemove(item.company)"
                            >
                                Quitar
                            </button>
                            <button
                                v-else
                                type="button"
                                class="row-action"
                                @click="item.state === 'baja' ? toggleRemove(item.company) : undoAdd(item.company.c_id)"
                            >
                                Deshacer
                            </button>
                        </div>
                    </div>
                </div>

                <div class="col col-search">
                    <p class="col-eyebrow">Agregar compañías</p>
                    <input
                        type="text"
                        class="search-input"
                        v-model="filterInput"
                        placeholder="Buscar por nombre o RUT"
                        aria-label="Buscar compañías para agregar"
                    />
                    <div class="col-scroll">
                        <div v-if="searchResults.length === 0" class="empty-state">
                            <template v-if="filterCompany.trim()">Sin resultados para «{{ filterCompany.trim() }}».</template>
                            <template v-else>No hay más compañías para agregar.</template>
                        </div>
                        <div v-for="company in searchResults" :key="company.c_id" class="company-row">
                            <div class="company-row__info">
                                <span class="company-row__name">{{ company.name }}</span>
                                <span class="company-row__meta">{{ company.rut }} · {{ company.email || 'sin usuarios' }}</span>
                            </div>
                            <button
                                type="button"
                                class="row-action row-action--add"
                                @click="addCompany(company)"
                            >
                                Agregar
                            </button>
                        </div>
                    </div>
                </div>
            </template>
        </section>

        <!-- Bandeja de cambios pendientes: cada chip deshace su cambio -->
        <footer class="modal-card-foot bt-none has-background-white">
            <div class="pending-tray" aria-live="polite">
                <template v-if="pendingCount">
                    <button
                        v-for="chip in trayChips"
                        :key="chip.key"
                        type="button"
                        class="tray-chip"
                        :class="chip.type === 'add' ? 'tray-chip--add' : 'tray-chip--remove'"
                        :title="'Deshacer: ' + chip.label"
                        @click="undoChip(chip)"
                    >
                        {{ chip.type === 'add' ? '+' : '−' }} {{ chip.label }}
                    </button>
                    <button type="button" class="tray-discard" @click="discardChanges()">
                        Descartar todo
                    </button>
                </template>
                <span v-else class="tray-empty">Sin cambios pendientes</span>
            </div>
            <div class="foot-actions">
                <button class="button" @click="$emit('close')" aria-label="Cerrar Modal" role="button">
                    Volver
                </button>
                <button
                    class="button is-primary"
                    :disabled="loading || pendingCount === 0"
                    role="button"
                    @click="guardarCambios()"
                >
                    {{ saveLabel }}
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
import { allAnalysts, allCompanies, saveAnalystCartera } from "@/api/carteraAnalista";
import { consoleHandlerError, notificate } from "@/utils/message";

// Tope de resultados de búsqueda renderizados: el listado puede traer miles;
// renderizarlos todos congela el modal (refinar con el filtro).
const MAX_RESULTS = 60;

export default {
    name: "AssignAnalysts",
    props: {
        // Email del analista logueado (PanelAnalista lo tiene de get_all):
        // se preselecciona para que "quitarme/tomarme una empresa" sea directo.
        currentAnalystEmail: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            analysts_list: [],
            raw_companies_list: [],
            selectedAnalystId: -1,
            // Cambios pendientes (ids de compañía): se acumulan y se guardan
            // todos juntos en un POST atómico (save_analyst_cartera).
            pendingAdd: [],
            pendingRemove: [],
            filterInput: "",
            filterCompany: "",
            filterTimer: null,
            // Resultados del picker server-side (all=true): incluye contrapartes
            // y compañías sin usuarios que el listado local no trae.
            serverResults: [],
            searchSeq: 0,
            loading: false,
        };
    },
    computed: {
        // El analista logueado primero; el resto en el orden del backend.
        orderedAnalysts() {
            return [...this.analysts_list].sort((a, b) => {
                return this.isCurrentUser(b) - this.isCurrentUser(a);
            });
        },
        selectedAnalyst() {
            return this.analysts_list.find(
                (user) => user.user_id === this.selectedAnalystId
            ) || null;
        },
        // Compañías hoy asignadas al analista elegido.
        carteraCompanies() {
            if (this.selectedAnalystId === -1) {
                return [];
            }
            return this.raw_companies_list
                .filter((company) =>
                    (company.analistas || []).some(
                        (a) => a.user_id === this.selectedAnalystId
                    )
                )
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        },
        // Cartera resultante: altas pendientes arriba, luego la cartera actual
        // (las bajas pendientes quedan visibles, tachadas, para poder deshacer).
        carteraView() {
            const added = this.pendingAdd
                .map((id) => this.raw_companies_list.find((c) => c.c_id === id))
                .filter(Boolean)
                .map((company) => ({ company, state: "nueva" }));
            const current = this.carteraCompanies.map((company) => ({
                company,
                state: this.pendingRemove.includes(company.c_id) ? "baja" : "actual",
            }));
            return [...added, ...current];
        },
        carteraCountLabel() {
            const now = this.carteraCompanies.length;
            const after = now + this.pendingAdd.length - this.pendingRemove.length;
            return after === now ? String(now) : `${now} → ${after}`;
        },
        // Buscador: excluye lo ya asignado y las altas pendientes. Con término,
        // suma los resultados server-side del picker (contrapartes y compañías
        // sin usuarios que no vienen en el listado local), deduplicados.
        searchResults() {
            if (this.selectedAnalystId === -1) {
                return [];
            }
            const term = this.filterCompany.trim().toUpperCase();
            const carteraIds = new Set(
                this.carteraCompanies.map((company) => company.c_id)
            );
            const excluded = (company) =>
                carteraIds.has(company.c_id) ||
                this.pendingAdd.includes(company.c_id);
            let list = this.raw_companies_list.filter((c) => !excluded(c));
            if (term) {
                list = list.filter((c) => (c.all_text || "").includes(term));
                const seen = new Set(list.map((c) => c.c_id));
                const extras = this.serverResults.filter(
                    (c) => !excluded(c) && !seen.has(c.c_id)
                );
                list = [...list, ...extras];
            }
            return list.slice(0, MAX_RESULTS);
        },
        pendingCount() {
            return this.pendingAdd.length + this.pendingRemove.length;
        },
        trayChips() {
            const nameOf = (id) => {
                const company = this.raw_companies_list.find((c) => c.c_id === id);
                return company ? company.name : `#${id}`;
            };
            return [
                ...this.pendingAdd.map((id) => ({
                    key: `add-${id}`, id, type: "add", label: nameOf(id),
                })),
                ...this.pendingRemove.map((id) => ({
                    key: `remove-${id}`, id, type: "remove", label: nameOf(id),
                })),
            ];
        },
        saveLabel() {
            if (this.pendingCount === 0) {
                return "Guardar";
            }
            return this.pendingCount === 1
                ? "Guardar 1 cambio"
                : `Guardar ${this.pendingCount} cambios`;
        },
    },
    methods: {
        isCurrentUser(user) {
            return Boolean(
                this.currentAnalystEmail &&
                user.user_email === this.currentAnalystEmail
            );
        },
        selectAnalyst(user) {
            if (user.user_id === this.selectedAnalystId) {
                return;
            }
            if (
                this.pendingCount > 0 &&
                !window.confirm("Hay cambios sin guardar. ¿Descartarlos y cambiar de analista?")
            ) {
                return;
            }
            this.selectedAnalystId = user.user_id;
            this.pendingAdd = [];
            this.pendingRemove = [];
        },
        toggleRemove(company) {
            const index = this.pendingRemove.indexOf(company.c_id);
            if (index === -1) {
                this.pendingRemove.push(company.c_id);
            } else {
                this.pendingRemove.splice(index, 1);
            }
        },
        addCompany(company) {
            // Resultado server-side (contraparte) que el listado local no
            // tiene: se incorpora para que la cartera, la bandeja y el guardado
            // local lo puedan resolver.
            if (!this.raw_companies_list.some((c) => c.c_id === company.c_id)) {
                this.raw_companies_list.push(company);
            }
            if (!this.pendingAdd.includes(company.c_id)) {
                this.pendingAdd.push(company.c_id);
            }
        },
        undoAdd(companyId) {
            const index = this.pendingAdd.indexOf(companyId);
            if (index !== -1) {
                this.pendingAdd.splice(index, 1);
            }
        },
        undoChip(chip) {
            if (chip.type === "add") {
                this.undoAdd(chip.id);
            } else {
                const index = this.pendingRemove.indexOf(chip.id);
                if (index !== -1) {
                    this.pendingRemove.splice(index, 1);
                }
            }
        },
        discardChanges() {
            this.pendingAdd = [];
            this.pendingRemove = [];
        },
        async guardarCambios() {
            if (this.pendingCount === 0 || this.selectedAnalystId === -1) {
                return;
            }
            this.loading = true;
            try {
                await saveAnalystCartera(
                    this.selectedAnalystId, this.pendingAdd, this.pendingRemove,
                );
                this.applyPendingLocally();
                notificate(
                    "<strong>Cartera actualizada</strong>",
                    "is-success is-light"
                );
                this.$emit("saved");
            } catch (error) {
                consoleHandlerError({
                    error,
                    customDefaultMessage: "No se pudo guardar la cartera",
                    empty: true,
                });
            } finally {
                this.loading = false;
            }
        },
        // Refleja el guardado en el listado local (sin re-fetch): mantiene el
        // modal usable para seguir editando esta u otra cartera.
        applyPendingLocally() {
            const email = this.selectedAnalyst ? this.selectedAnalyst.user_email : "";
            this.pendingAdd.forEach((id) => {
                const company = this.raw_companies_list.find((c) => c.c_id === id);
                if (!company) {
                    return;
                }
                // $set: `analistas` puede no existir aún en el objeto (Vue 2).
                if (!company.analistas) {
                    this.$set(company, "analistas", []);
                }
                company.analistas.push({
                    user_id: this.selectedAnalystId, email,
                });
            });
            this.pendingRemove.forEach((id) => {
                const company = this.raw_companies_list.find((c) => c.c_id === id);
                if (!company || !company.analistas) {
                    return;
                }
                this.$set(
                    company, "analistas",
                    company.analistas.filter(
                        (a) => a.user_id !== this.selectedAnalystId
                    ),
                );
            });
            this.pendingAdd = [];
            this.pendingRemove = [];
        },
        preselectCurrentAnalyst() {
            const me = this.analysts_list.find((user) => this.isCurrentUser(user));
            if (me) {
                this.selectedAnalystId = me.user_id;
            }
        },
    },
    mounted() {
        allAnalysts().then((response) => {
            this.analysts_list = response.data;
            this.preselectCurrentAnalyst();
        }).catch((error) => {
            consoleHandlerError({ error, empty: true });
        });
        allCompanies().then((response) => {
            this.raw_companies_list = response.data;
        }).catch((error) => {
            consoleHandlerError({ error, empty: true });
        });
    },
    beforeDestroy() {
        clearTimeout(this.filterTimer);
    },
    watch: {
        // Debounce: no re-filtrar el listado completo en cada tecla.
        filterInput(value) {
            clearTimeout(this.filterTimer);
            this.filterTimer = setTimeout(() => {
                this.filterCompany = value;
            }, 180);
        },
        // Búsqueda server-side (picker all=true) en paralelo al filtro local:
        // trae contrapartes/compañías sin usuarios. searchSeq descarta
        // respuestas fuera de orden.
        filterCompany(value) {
            const term = (value || "").trim();
            const seq = ++this.searchSeq;
            // Mínimo 2 caracteres: con 1 el ILIKE server-side barre ~100k
            // contrapartes para devolver 50 matches inútiles. El filtro local
            // sigue aplicando desde el primer carácter.
            if (term.length < 2) {
                this.serverResults = [];
                return;
            }
            allCompanies(true, term).then((response) => {
                if (seq === this.searchSeq) {
                    this.serverResults = response.data;
                }
            }).catch(() => {
                // Búsqueda auxiliar: si falla, el filtro local sigue sirviendo.
            });
        },
    },
};
</script>
<style scoped lang="scss">
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.cartera-modal {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
    width: min(940px, 94vw);
    display: flex;
    flex-direction: column;
    max-height: 86vh;
    background-color: var(--ap-paper);
    border-radius: var(--ap-radius);
    overflow: hidden;
}

.modal-card-head {
    border-bottom: 1px solid var(--ap-line);
    flex-shrink: 0;
}

:deep(.modal-card-title){
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.01em;
    color: var(--ap-ink);
}

.col-eyebrow {
    margin: 0 0 10px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ap-muted);
}

// Analistas ------------------------------------------------------------------
.section-analysts {
    padding: 16px 22px;
    background-color: var(--ap-mist);
    border-bottom: 1px solid var(--ap-line);
    max-height: 150px;
    overflow: hidden auto;
    // No encoger: sin esto flexbox aplasta la sección cuando el cuerpo no
    // entra en 86vh y los chips quedan recortados a media altura.
    flex-shrink: 0;
}

.analyst-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.analyst-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-paper);
    color: var(--ap-ink-2);
    font-family: var(--ap-font);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: transform 0.14s var(--ap-ease), border-color 0.14s var(--ap-ease),
        background-color 0.14s var(--ap-ease);

    &:hover {
        transform: translateY(-1px);
        border-color: var(--ap-teal);
        color: var(--ap-teal-deep);
    }

    &:focus-visible {
        outline: 2px solid var(--ap-teal);
        outline-offset: 2px;
    }

    &.is-selected {
        background-color: var(--ap-teal);
        border-color: var(--ap-teal);
        color: #fff;

        .chip-me {
            background-color: rgba(255, 255, 255, 0.22);
            color: #fff;
        }
    }
}

.chip-me {
    padding: 1px 7px;
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-teal-wash);
    color: var(--ap-teal-deep);
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

// Cuerpo: cartera | buscador ---------------------------------------------------
.section-body {
    flex: 1;
    min-height: 0;
    display: grid;
    // minmax(0, 1fr) y no 1fr: los emails/nombres con nowrap fuerzan el
    // min-content de la columna y el grid desborda la card (mismo fix que
    // .cbd__grid en CompanyInfo_BasicData).
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    background-color: var(--ap-paper);
}

.col {
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    padding: 16px 22px 18px;
}

.col-cartera {
    border-right: 1px solid var(--ap-line);
}

.col-scroll {
    flex: 1;
    min-height: 0;
    overflow: hidden auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.search-input {
    width: 100%;
    margin-bottom: 12px;
    padding: 8px 12px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-sm);
    font-family: var(--ap-font);
    font-size: 13.5px;
    color: var(--ap-ink);
    transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);

    &:focus {
        outline: none;
        border-color: var(--ap-teal);
        box-shadow: 0 0 0 3px var(--ap-teal-wash);
    }
}

// Fila de compañía -------------------------------------------------------------
.company-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-sm);
    background-color: var(--ap-paper);
    transition: border-color 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease);

    &:hover {
        border-color: var(--ap-line-strong);
    }

    &.is-added {
        background-color: var(--ap-ok-wash);
        border-color: rgba(15, 157, 107, 0.25);
    }

    &.is-removed {
        background-color: var(--ap-alert-wash);
        border-color: rgba(192, 69, 59, 0.25);

        .company-row__name {
            text-decoration: line-through;
            text-decoration-thickness: 2px;
            color: var(--ap-alert);
        }
    }
}

.company-row__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.company-row__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ap-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.company-row__meta {
    font-size: 11.5px;
    color: var(--ap-slate);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.state-tag {
    flex-shrink: 0;
    padding: 2px 9px;
    border-radius: var(--ap-radius-pill);
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.state-tag--add {
    background-color: var(--ap-ok-wash);
    color: var(--ap-ok-ink);
}

.row-action {
    flex-shrink: 0;
    padding: 4px 12px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-paper);
    color: var(--ap-slate);
    font-family: var(--ap-font);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease),
        background-color 0.14s var(--ap-ease);

    &:hover {
        border-color: var(--ap-teal);
        color: var(--ap-teal-deep);
    }

    &:focus-visible {
        outline: 2px solid var(--ap-teal);
        outline-offset: 1px;
    }
}

.row-action--remove:hover {
    border-color: var(--ap-alert);
    color: var(--ap-alert);
    background-color: var(--ap-alert-wash);
}

.row-action--add {
    border-color: rgba(15, 157, 107, 0.35);
    color: var(--ap-ok-ink);

    &:hover {
        border-color: var(--ap-ok);
        color: var(--ap-ok-ink);
        background-color: var(--ap-ok-wash);
    }
}

// Estados vacíos ----------------------------------------------------------------
.empty-state {
    padding: 26px 10px;
    text-align: center;
    color: var(--ap-muted);
    font-size: 13px;
}

.empty-state--body {
    grid-column: 1 / -1;
    align-self: center;
}

// Footer: bandeja de cambios + acciones -------------------------------------------
:deep(.modal-card-foot){
    border-top: 1px solid var(--ap-line);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    flex-shrink: 0;

    .button.is-primary {
        background-color: var(--ap-ink);
        border-color: transparent;
        font-weight: 600;

        &:hover {
            background-color: var(--ap-teal-deep);
        }
    }
}

.pending-tray {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.tray-empty {
    font-size: 12px;
    color: var(--ap-muted);
}

.tray-chip {
    max-width: 190px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 3px 11px;
    border-radius: var(--ap-radius-pill);
    border: 1px solid transparent;
    font-family: var(--ap-font);
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.14s var(--ap-ease);
}

.tray-chip--add {
    background-color: var(--ap-ok-wash);
    color: var(--ap-ok-ink);
    border-color: rgba(15, 157, 107, 0.22);

    &:hover {
        border-color: var(--ap-ok);
    }
}

.tray-chip--remove {
    background-color: var(--ap-alert-wash);
    color: var(--ap-alert);
    border-color: rgba(192, 69, 59, 0.22);

    &:hover {
        border-color: var(--ap-alert);
    }
}

.tray-discard {
    border: none;
    background: transparent;
    padding: 3px 6px;
    font-family: var(--ap-font);
    font-size: 11.5px;
    font-weight: 600;
    color: var(--ap-muted);
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: var(--ap-alert);
    }
}

.foot-actions {
    display: flex;
    gap: 10px;
}

@media (max-width: 760px) {
    .section-body {
        grid-template-columns: 1fr;
        overflow: hidden auto;
    }

    .col-cartera {
        border-right: none;
        border-bottom: 1px solid var(--ap-line);
    }

    .col-scroll {
        max-height: 240px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .analyst-chip,
    .company-row,
    .row-action,
    .tray-chip {
        transition: none;
    }
}
</style>
